import { useEffect } from 'react';
import { useTemplateStore } from './useTemplateStore';
import { useTemplatePermissions } from './useTemplatePermissions';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Permission } from '@/lib/api/interfaces/permissions.interface';

export const useKeyboardShortcuts = (onSave?: () => void) => {
  const { canEditTemplate } = useTemplatePermissions();
  const { can } = usePermissions();
  const copyElement = useTemplateStore(s => s.copyElement);
  const pasteElement = useTemplateStore(s => s.pasteElement);
  const duplicateElement = useTemplateStore(s => s.duplicateElement);
  const deleteElement = useTemplateStore(s => s.deleteElement);
  const selectedElementId = useTemplateStore(s => s.selectedElementId);
  const undo = useTemplateStore(s => s.undo);
  const redo = useTemplateStore(s => s.redo);
  
  const moveElementUp = useTemplateStore(s => s.moveElementUp);
  const moveElementDown = useTemplateStore(s => s.moveElementDown);
  const moveElementToTop = useTemplateStore(s => s.moveElementToTop);
  const moveElementToBottom = useTemplateStore(s => s.moveElementToBottom);

  useEffect(() => {
    const hasEditPermission = canEditTemplate;
    const canManageEntities = can(Permission.MANAGE_ENTITIES);

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable
      ) {
        return;
      }

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      const isShift = e.shiftKey;

      // Save: Cmd+S
      if (isCmdOrCtrl && e.key === 's') {
        e.preventDefault();
        if (canManageEntities) onSave?.();
        return;
      }

      // Undo: Cmd+Z
      if (isCmdOrCtrl && !isShift && e.key === 'z') {
        e.preventDefault();
        if (canManageEntities) undo();
        return;
      }

      // Redo: Cmd+Shift+Z or Cmd+Y
      if ((isCmdOrCtrl && isShift && e.key === 'z') || (isCmdOrCtrl && e.key === 'y')) {
        e.preventDefault();
        if (canManageEntities) redo();
        return;
      }

      // Copy: Cmd+C
      if (isCmdOrCtrl && e.key === 'c') {
        e.preventDefault();
        if (hasEditPermission) copyElement();
        return;
      }

      // Paste: Cmd+V
      if (isCmdOrCtrl && e.key === 'v') {
        e.preventDefault();
        if (hasEditPermission) pasteElement();
        return;
      }

      // Duplicate: Cmd+D
      if (isCmdOrCtrl && e.key === 'd') {
        e.preventDefault();
        if (hasEditPermission) duplicateElement();
        return;
      }

      // Delete: Backspace or Delete
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selectedElementId && hasEditPermission) {
          e.preventDefault();
          deleteElement(selectedElementId);
        }
        return;
      }

      // Layer Movement
      if (isCmdOrCtrl && selectedElementId && canManageEntities) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (isShift) {
            moveElementToTop(selectedElementId);
          } else {
            moveElementUp(selectedElementId);
          }
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (isShift) {
            moveElementToBottom(selectedElementId);
          } else {
            moveElementDown(selectedElementId);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    onSave, 
    copyElement, 
    pasteElement, 
    duplicateElement, 
    deleteElement, 
    selectedElementId, 
    undo, 
    redo,
    moveElementUp,
    moveElementDown,
    moveElementToTop,
    moveElementToBottom,
    canEditTemplate,
    can
  ]);
};
